import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutData } from '@/mocks/about';

export default function AboutPage() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  // Schema.org JSON-LD
  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: aboutData.brandName,
    url: 'https://www.blueberryvideo.com',
    description: aboutData.description,
    foundingDate: '2019',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: aboutData.contact.phone,
      email: aboutData.contact.email,
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: aboutData.contact.address,
    },
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${aboutData.brandName}`,
    description: `${aboutData.brandName}品牌介绍、发展历程与联系方式`,
    url: 'https://www.blueberryvideo.com/about',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutData.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            {/* Page Header */}
            <div className="py-8 md:py-10 text-center">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading">
                关于蓝莓视频
              </h1>
              <p className="text-sm md:text-base text-foreground-500 mt-3 max-w-2xl mx-auto">
                {aboutData.slogan}
              </p>
            </div>

            {/* Brand Story */}
            <section className="pb-10">
              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200">
                <h2 className="text-xl font-bold text-foreground-900 mb-4">品牌故事</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {aboutData.description}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                      <i className="ri-focus-2-line text-primary-500"></i>
                      我们的使命
                    </h3>
                    <p className="text-sm text-foreground-500">{aboutData.mission}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                      <i className="ri-eye-line text-primary-500"></i>
                      我们的愿景
                    </h3>
                    <p className="text-sm text-foreground-500">{aboutData.vision}</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Stats */}
            <section className="pb-10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {aboutData.stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="bg-background-100 rounded-xl p-5 text-center border border-background-200"
                  >
                    <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">
                      {stat.value}
                    </div>
                    <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Core Values */}
            <section className="pb-10">
              <h2 className="text-xl font-bold text-foreground-900 mb-5 text-center">核心价值观</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {aboutData.values.map((val) => (
                  <div
                    key={val.title}
                    className="bg-background-100 rounded-xl p-5 border border-background-200 hover:border-primary-500/30 transition-all"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center mb-3">
                      <i className={`${val.icon} text-primary-500 text-lg`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">{val.title}</h3>
                    <p className="text-xs text-foreground-500">{val.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Milestones */}
            <section className="pb-10">
              <h2 className="text-xl font-bold text-foreground-900 mb-5 text-center">发展历程</h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2 hidden md:block"></div>
                <div className="space-y-6">
                  {aboutData.milestones.map((item, index) => (
                    <div key={item.year} className={`flex flex-col md:flex-row gap-4 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="flex-1 hidden md:block"></div>
                      <div className="relative flex items-center justify-center">
                        <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center z-10 shrink-0">
                          <span className="text-xs font-bold text-foreground-950">{item.year.slice(2)}</span>
                        </div>
                      </div>
                      <div className="flex-1 bg-background-100 rounded-xl p-4 border border-background-200">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-primary-500">{item.year}</span>
                          <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                        </div>
                        <p className="text-xs text-foreground-500">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section className="pb-10">
              <h2 className="text-xl font-bold text-foreground-900 mb-5 text-center">常见问题</h2>
              <div className="max-w-3xl mx-auto space-y-3">
                {aboutData.faqs.map((faq, index) => (
                  <details key={index} className="group bg-background-100 rounded-xl border border-background-200 overflow-hidden">
                    <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                      <h3 className="text-sm font-medium text-foreground-900 pr-8">{faq.question}</h3>
                      <div className="w-6 h-6 rounded-full bg-background-300 flex items-center justify-center shrink-0 group-open:rotate-180 transition-transform">
                        <i className="ri-arrow-down-s-line text-foreground-600 text-sm"></i>
                      </div>
                    </summary>
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section className="pb-12">
              <h2 className="text-xl font-bold text-foreground-900 mb-5 text-center">联系我们</h2>
              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200 max-w-3xl mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center shrink-0">
                      <i className="ri-mail-line text-primary-500"></i>
                    </div>
                    <div>
                      <h3 className="text-xs font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                      <p className="text-sm text-foreground-500">{aboutData.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center shrink-0">
                      <i className="ri-phone-line text-primary-500"></i>
                    </div>
                    <div>
                      <h3 className="text-xs font-semibold text-foreground-900 mb-1">客服电话</h3>
                      <p className="text-sm text-foreground-500">{aboutData.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center shrink-0">
                      <i className="ri-wechat-line text-primary-500"></i>
                    </div>
                    <div>
                      <h3 className="text-xs font-semibold text-foreground-900 mb-1">微信公众号</h3>
                      <p className="text-sm text-foreground-500">{aboutData.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center shrink-0">
                      <i className="ri-map-pin-line text-primary-500"></i>
                    </div>
                    <div>
                      <h3 className="text-xs font-semibold text-foreground-900 mb-1">公司地址</h3>
                      <p className="text-sm text-foreground-500">{aboutData.contact.address}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-6 pt-4 border-t border-background-200 flex items-center gap-2 text-xs text-foreground-400">
                  <i className="ri-time-line"></i>
                  工作时间：{aboutData.contact.workHours}
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}